import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, PlusCircle, Search, Eye, Pencil, Trash2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';

const STATUS_COLORS = {
    New: 'bg-blue-100 text-blue-800',
    Contacted: 'bg-indigo-100 text-indigo-800',
    Qualified: 'bg-purple-100 text-purple-800',
    Pending: 'bg-yellow-100 text-yellow-800',
    Converted: 'bg-green-100 text-green-800',
    Lost: 'bg-red-100 text-red-800',
};

const STATUSES = ['', 'New', 'Contacted', 'Qualified', 'Pending', 'Converted', 'Lost'];

export default function LeadList() {
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [referralSources, setReferralSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [referralSourceId, setReferralSourceId] = useState('');
    const [deleting, setDeleting] = useState(null);

    const fetchLeads = async () => {
        setLoading(true);
        try {
            const params = {};
            if (status) params.status = status;
            if (referralSourceId) params.referral_source_id = referralSourceId;
            const res = search
                ? await api.get('/leads/search', { params: { q: search, ...params } })
                : await api.get('/leads', { params });
            setLeads(res.data.data || res.data || []);
        } catch {
            toast.error('Failed to load leads');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Load referral sources for filter
        api.get('/referral-sources').then(res => setReferralSources(res.data || []));
    }, []);

    useEffect(() => { fetchLeads(); }, [status, referralSourceId]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchLeads();
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this lead?')) return;
        setDeleting(id);
        try {
            await api.delete(`/leads/${id}`);
            setLeads(prev => prev.filter(l => l.id !== id));
            toast.success('Lead deleted');
        } catch {
            toast.error('Failed to delete lead');
        } finally {
            setDeleting(null);
        }
    };

    return (
        <div className="space-y-6">
            <Toaster position="top-right" richColors />
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Leads</h2>
                        <p className="text-gray-500 text-sm mt-1">Manage all CRM leads</p>
                    </div>
                    <button
                        onClick={() => navigate('/admin/leads/new')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                    >
                        <PlusCircle className="w-4 h-4" /> Add Lead
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <form onSubmit={handleSearch} className="flex gap-2 flex-1">
                        <input
                            type="text"
                            placeholder="Search by name, phone, or email…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ color: '#000000', backgroundColor: '#ffffff' }}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button type="submit" className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                            <Search className="w-4 h-4 text-gray-500" />
                        </button>
                    </form>
                    <select
                        value={status}
                        onChange={e => { setStatus(e.target.value); setSearch(''); }}
                        style={{ color: '#000000', backgroundColor: '#ffffff', colorScheme: 'light' }}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    >
                        {STATUSES.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
                    </select>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>
                ) : (
                    <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {['Name', 'Phone', 'Service', 'Referrer', 'Status', 'Assigned Staff', 'Created', 'Actions'].map(h => (
                                            <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {leads.length === 0 ? (
                                        <tr><td colSpan={8} className="px-5 py-10 text-center text-gray-400">No leads found</td></tr>
                                    ) : leads.map(lead => (
                                        <tr key={lead.id} className="hover:bg-gray-50">
                                            <td className="px-5 py-3 font-medium text-gray-900">{lead.name}</td>
                                            <td className="px-5 py-3 text-sm text-gray-600">{lead.phone}</td>
                                            <td className="px-5 py-3 text-sm text-gray-600">{lead.service_requested}</td>
                                            <td className="px-5 py-3 text-sm text-gray-600">
                                                {lead.referral_source ? (
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">{lead.referral_source.facility_name}</span>
                                                        <span className="text-xs text-gray-400">{lead.referral_source.contact_person}</span>
                                                    </div>
                                                ) : '—'}
                                            </td>
                                            <td className="px-5 py-3">
                                                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[lead.status] || 'bg-gray-100 text-gray-700'}`}>
                                                    {lead.status}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-sm text-gray-600">{lead.assigned_staff?.name || '—'}</td>
                                            <td className="px-5 py-3 text-sm text-gray-500">{new Date(lead.created_at).toLocaleDateString()}</td>
                                            <td className="px-5 py-3">
                                                <div className="flex gap-2">
                                                    <button onClick={() => navigate(`/admin/leads/${lead.id}`)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md"><Eye className="w-4 h-4" /></button>
                                                    <button onClick={() => navigate(`/admin/leads/${lead.id}/edit`)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-md"><Pencil className="w-4 h-4" /></button>
                                                    <button onClick={() => handleDelete(lead.id)} disabled={deleting === lead.id} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50">
                                                        {deleting === lead.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
